/**
 * Plain-JS drag module — replaces useDrag Vue composable.
 *
 * @param {HTMLElement} panelEl  - The element being dragged (overlay)
 * @param {HTMLElement} handleEl - The drag-handle element that receives pointer events
 * @returns {{ resetPosition, getDragStyle, destroy }}
 */
export function initDrag(panelEl, handleEl) {
  let pos = null;
  let dragging = false;
  const offset = { x: 0, y: 0 };

  function onPointerDown(e) {
    const tag = e.target.tagName;
    if (tag === "BUTTON" || tag === "INPUT" || tag === "A") return;
    if (!panelEl) return;

    dragging = true;
    const rect = panelEl.getBoundingClientRect();

    const currentX = pos ? pos.x : rect.left;
    const currentY = pos ? pos.y : rect.top;

    offset.x = e.clientX - currentX;
    offset.y = e.clientY - currentY;

    e.target.setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  function onPointerMove(e) {
    if (!dragging) return;
    if (!panelEl) return;

    const rect = panelEl.getBoundingClientRect();
    let newX = e.clientX - offset.x;
    let newY = e.clientY - offset.y;

    const maxX = window.innerWidth - rect.width;
    const maxY = window.innerHeight - rect.height;
    newX = Math.max(0, Math.min(newX, maxX));
    newY = Math.max(0, Math.min(newY, maxY));

    pos = { x: newX, y: newY };
    applyStyle();
  }

  function onPointerUp() {
    dragging = false;
  }

  function applyStyle() {
    if (!panelEl) return;
    if (!pos) {
      panelEl.style.left = "";
      panelEl.style.top = "";
      panelEl.style.bottom = "";
      panelEl.style.right = "";
      return;
    }
    panelEl.style.position = "fixed";
    panelEl.style.left = pos.x + "px";
    panelEl.style.top = pos.y + "px";
    panelEl.style.bottom = "auto";
    panelEl.style.right = "auto";
    panelEl.style.zIndex = "999998";
  }

  function resetPosition() {
    pos = null;
    if (panelEl) {
      panelEl.style.left = "";
      panelEl.style.top = "";
      panelEl.style.bottom = "";
      panelEl.style.right = "";
      panelEl.style.position = "";
    }
  }

  function getDragStyle() {
    if (!pos) return undefined;
    return {
      position: "fixed",
      left: pos.x + "px",
      top: pos.y + "px",
      bottom: "auto",
      right: "auto",
      zIndex: 999998,
    };
  }

  // Attach events
  handleEl.addEventListener("pointerdown", onPointerDown);
  handleEl.addEventListener("pointermove", onPointerMove);
  handleEl.addEventListener("pointerup", onPointerUp);

  function destroy() {
    handleEl.removeEventListener("pointerdown", onPointerDown);
    handleEl.removeEventListener("pointermove", onPointerMove);
    handleEl.removeEventListener("pointerup", onPointerUp);
  }

  return { resetPosition, getDragStyle, destroy };
}
